import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import UserFormEdit from '../UserFormEdit';
import UserFormCreate from '../UserFormCreate';
import PasswordUpdateModal from '../PasswordUpdateModal';
import UserStatusToggleModal from '../UserStatusToggleModal';
import api3000 from '../../services/api3000';
import { useNavigate } from 'react-router-dom';
import Swal from "sweetalert2";

const Title = styled.h1`
  font-size: 28px;
  margin-bottom: 20px;
  font-family: 'Arial', sans-serif;
  color: #333;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 24px;
  }

  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const UserTableWrapper = styled.div`
  overflow-x: auto;
  margin-top: 20px;
  
  @media (max-width: 768px) {
    overflow-x: hidden;
  }
`;

const UserTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  @media (max-width: 768px) {
    display: none; /* Esconde a tabela original em telas pequenas */
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const TableHead = styled.thead`
  background-color: #151e3d;
  color: white;
  text-align: left;
`;

const TableHeadCell = styled.th`
  padding: 10px;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #d3d3d3;
  opacity: ${(props) => (props.isInactive ? 0.5 : 1)};
`;

const TableCell = styled.td`
  padding: 10px;
  text-align: left;
`;

const Button = styled.button`
  margin: 2px;
  padding: ${({$smal}) => ($smal ? "3px 6px" : "6px 12px")};
  font-size: ${({$smal}) => ($smal ? "11px" : "14px")};
  cursor: pointer;
  border: 1px solid #007bff;
  border-radius: 6px;
  color: #007bff;
  background-color: transparent;
  white-space: nowrap;
  transition: background-color 0.3s ease, transform 0.2s ease;

  &:hover {
    background-color: #007bff;
    color: white;
  }

  &:active {
    transform: scale(0.98);
    background-color: #0056b3;
  }
`;

const EditButton = styled(Button)`
  color: #007bff;
  border-color: #007bff;

  &:hover {
    background-color: #007bff;
    color: white;
    border-color: #007bff;
  }

  &:active {
    background-color: #0056b3;
    border-color: #0056b3;
  }
`;

const InactivateButton = styled(Button)`
  color: red;
  border-color: red;

  &:hover {
    background-color: red;
    color: white;
    border-color: red;
  }

  &:active {
    background-color: darkred;
    border-color: darkred;
  }
`;

const ActivateButton = styled(Button)`
  color: green;
  border-color: green;

  &:hover {
    background-color: green;
    color: white;
    border-color: green;
  }

  &:active {
    background-color: darkgreen;
    border-color: darkgreen;
  }
`;

const CreateButton = styled(Button)`
  color: #007bff;
  border-color: #007bff;

  &:hover {
    background-color: #007bff;
    color: white;
    border-color: #007bff;
  }

  &:active {
    background-color: #0056b3;
    border-color: #0056b3;
  }
`;


const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

// Estilo para tabelas responsivas em telas pequenas (div de cartão)
const CardWrapper = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block; /* Exibe as linhas de dados como "cartões" em telas pequenas */
    margin-top: 20px;
  }
`;

const UserCard = styled.div`
  background-color: #f8f9fa;
  padding: 20px;
  margin-bottom: 15px;
  border-radius: 8px;
  border: 1px solid #ddd;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const UserCardItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;

  & > span {
    font-weight: bold;
    color: #333;
  }
`;

const UserCardValue = styled.div`
  background-color: #e9ecef;
  padding: 5px 10px;
  border-radius: 4px;
  color: #495057;
  font-size: 14px;
`;

const CenteredContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
`;

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [passwordUserId, setPasswordUserId] = useState(null);
  const [statusToggleUser, setStatusToggleUser] = useState(null);
  const [createButtonClicked, setCreateButtonClicked] = useState(false);

  const navigate = useNavigate();

  const fetchUsers = () => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');

    if (!token) {
      Swal.fire({
        icon: "warning",
        title: "Conexão expirada",
        text: "Por favor, faça login novamente.",
        confirmButtonText: "OK"
      }).then(() => {
        navigate("/");
      });
      return;
    }

    if (role !== 'admin') {
      setError('Você precisa ser admin para acessar esta página.');
      return;
    }

    api3000.get('/api/users', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        setUsers(response.data);
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          setError('Acesso proibido: Token inválido ou expirado.');
        } else {
          setError(`Erro: ${error.response?.statusText || error.message}`);
        }
        console.error('Erro ao buscar usuários:', error);
      });
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreateButtonClick = () => {
    setCreateButtonClicked(true);
    setShowCreateModal(true);
  };

  return (
    <div>
      <Title>Usuários</Title>
      <CenteredContainer>
        <CreateButton
          onClick={handleCreateButtonClick}
          className={createButtonClicked ? 'clicked' : ''}
        >
          Criar Usuário
        </CreateButton>
      </CenteredContainer>
      
      {error && <p style={{ color: 'red', textAlign: 'center' }}>{error}</p>}

      {/* Tabela Desktop */}
      <UserTableWrapper>
        <UserTable>
          <TableHead>
            <tr>
              <TableHeadCell>ID</TableHeadCell>
              <TableHeadCell>Nome</TableHeadCell>
              <TableHeadCell>Email</TableHeadCell>
              <TableHeadCell>Perfil</TableHeadCell>
              <TableHeadCell>Status</TableHeadCell>
              <TableHeadCell></TableHeadCell>
            </tr>
          </TableHead>
          <tbody>
            {users.map((user) => (
              <TableRow key={user.id} isInactive={user.status === 'inativo'}>
                <TableCell>{user.id}</TableCell>
                <TableCell>{user.nome}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.nivel_acesso}</TableCell>
                <TableCell>{user.status}</TableCell>
                <TableCell>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <EditButton onClick={() => setEditingUser(user)}>Editar</EditButton>
                    <EditButton onClick={() => setPasswordUserId(user.id)}>Alterar Senha</EditButton>
                    {user.status === 'inativo' ? (
                      <ActivateButton onClick={() => setStatusToggleUser(user)}>Ativar</ActivateButton>
                    ) : (
                      <InactivateButton onClick={() => setStatusToggleUser(user)}>Inativar</InactivateButton>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </UserTable>
      </UserTableWrapper>

      {/* Cartões para telas pequenas */}
      <CardWrapper>
        {users.map((user) => (
          <UserCard key={user.id}>
            <UserCardItem>
              <span>ID:</span> <UserCardValue>{user.id}</UserCardValue>
            </UserCardItem>
            <UserCardItem>
              <span>Nome:</span> <UserCardValue>{user.nome}</UserCardValue>
            </UserCardItem>
            <UserCardItem>
              <span>Email:</span> <UserCardValue>{user.email}</UserCardValue>
            </UserCardItem>
            <UserCardItem>
              <span>Perfil:</span> <UserCardValue>{user.nivel_acesso}</UserCardValue>
            </UserCardItem>
            <UserCardItem>
              <span>Status:</span> <UserCardValue>{user.status}</UserCardValue>
            </UserCardItem>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <EditButton onClick={() => setEditingUser(user)}>Editar</EditButton>
              <EditButton onClick={() => setPasswordUserId(user.id)}>Alterar Senha</EditButton>
              {user.status === 'inativo' ? (
                <ActivateButton onClick={() => setStatusToggleUser(user)}>Ativar</ActivateButton>
              ) : (
                <InactivateButton onClick={() => setStatusToggleUser(user)}>Inativar</InactivateButton>
              )}
            </div>
          </UserCard>
        ))}
      </CardWrapper>

      {/* Modais */}
      {editingUser && (
        <ModalOverlay>
          <UserFormEdit user={editingUser} onClose={() => setEditingUser(null)} onUpdated={fetchUsers} />
        </ModalOverlay>
      )}

      {showCreateModal && (
        <ModalOverlay>
          <UserFormCreate onClose={() => setShowCreateModal(false)} onCreated={fetchUsers} />
        </ModalOverlay>
      )}

      {statusToggleUser && (
        <ModalOverlay>
          <UserStatusToggleModal user={statusToggleUser} type='user' onClose={() => setStatusToggleUser(null)} onUpdated={fetchUsers} />
        </ModalOverlay>
      )}

      {passwordUserId && (
        <ModalOverlay>
          <PasswordUpdateModal userId={passwordUserId} onClose={() => setPasswordUserId(null)} onUpdated={fetchUsers} />
        </ModalOverlay>
      )}
    </div>
  );
};

export default UserList;
